import { useCallback, useEffect, useRef, useState } from "react";

// Continuous Web Audio siren — one long-running oscillator (gesture/clock driven),
// immune to background-tab setInterval throttling. Plays until stop() is called.
export const useSiren = () => {
  const [enabled, setEnabled] = useState(true);
  const [active, setActive] = useState(false);
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const enabledRef = useRef(true);

  const BASE_LEVEL = 0.25;
  const MAX_LEVEL = 0.9;
  const ESCALATE_SECONDS = 25; // ramps from BASE to MAX while unacknowledged

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const stop = useCallback(() => {
    const n = nodesRef.current;
    if (n && ctxRef.current) {
      const now = ctxRef.current.currentTime;
      try {
        n.master.gain.cancelScheduledValues(now);
        n.master.gain.setValueAtTime(Math.max(0.0001, n.master.gain.value), now);
        n.master.gain.linearRampToValueAtTime(0.0001, now + 0.08);
        n.carrier.stop(now + 0.12);
        n.lfo.stop(now + 0.12);
      } catch (e) { /* nodes may already be stopped */ }
      nodesRef.current = null;
    }
    setActive(false);
  }, []);

  const start = useCallback(() => {
    if (!enabledRef.current) return;
    const ctx = ensureCtx();
    if (nodesRef.current) { setActive(true); return; } // already wailing
    const now = ctx.currentTime;

    // Volume envelope: quick fade-in, then escalate toward MAX while active
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.linearRampToValueAtTime(BASE_LEVEL, now + 0.1);
    master.gain.linearRampToValueAtTime(MAX_LEVEL, now + ESCALATE_SECONDS);
    master.connect(ctx.destination);

    // Carrier tone
    const carrier = ctx.createOscillator();
    carrier.type = "sawtooth";
    carrier.frequency.setValueAtTime(850, now);

    // LFO sweeps the carrier frequency up/down -> continuous "wail"
    const lfo = ctx.createOscillator();
    lfo.type = "triangle";
    lfo.frequency.setValueAtTime(0.8, now); // wail speed (Hz)
    const lfoGain = ctx.createGain();
    lfoGain.gain.setValueAtTime(260, now); // sweep depth (Hz)
    lfo.connect(lfoGain);
    lfoGain.connect(carrier.frequency);

    carrier.connect(master);
    carrier.start(now);
    lfo.start(now);

    nodesRef.current = { master, carrier, lfo, lfoGain };
    setActive(true);
  }, [ensureCtx]);

  const toggle = useCallback(() => {
    setEnabled((e) => {
      const next = !e;
      enabledRef.current = next;
      if (!next) stop(); // muting silences an active siren
      else ensureCtx();
      return next;
    });
  }, [ensureCtx, stop]);

  // prime the audio context on first user gesture (autoplay policy)
  useEffect(() => {
    const prime = () => ensureCtx();
    window.addEventListener("click", prime, { once: true });
    return () => window.removeEventListener("click", prime);
  }, [ensureCtx]);

  // resume the context when returning to the tab (keeps siren audible)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && nodesRef.current && ctxRef.current?.state === "suspended") {
        ctxRef.current.resume();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // cleanup on unmount
  useEffect(() => () => { stop(); }, [stop]);

  return { enabled, active, toggle, start, stop };
};
